import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { IndianRupee, Percent, TrendingUp, Building2, Layers, ExternalLink } from "lucide-react";

/**
 * Iter47 — Aggregated Dashboard for chain owners.
 *
 * Renders the top-3 KPIs (revenue, bookings, occupancy%) computed server-side
 * across every property the current partner owns, plus a per-property
 * leaderboard sorted by revenue.
 *
 * This component is embedded in the Partner Dashboard when the property
 * switcher is set to "All Properties" (selectedId === "__all__").
 */
export default function AggregateDashboard() {
  const navigate = useNavigate();
  const { properties, select } = useProperty();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  useEffect(() => {
    setLoading(true);
    // Remove the X-Property-Id header temporarily by using the query param.
    // Actually the header still identifies which property to scope to, but
    // /my/dashboard/aggregate ignores it and returns cross-property data anyway.
    api.get(`/my/dashboard/aggregate?days=${days}`)
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [days]);

  if (loading) return <div className="hw-card p-10 text-center text-slate-500">Loading chain metrics…</div>;
  if (!data) return <div className="hw-card p-10 text-center text-slate-500">Could not load aggregate metrics.</div>;

  const gotoProperty = (hid) => {
    select(hid);
    navigate("/partner");
  };

  return (
    <div className="space-y-6" data-testid="aggregate-dashboard">
      <div className="hw-card p-6">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="hw-label text-emerald-700 flex items-center gap-1"><Layers className="w-3 h-3" /> Chain overview</div>
            <h2 className="hw-head text-2xl font-black text-slate-900 mt-1">{data.property_count} propert{data.property_count === 1 ? "y" : "ies"} · last {data.days} days</h2>
          </div>
          <div className="flex items-center gap-1 text-sm">
            {[7, 30, 90].map((d) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={`px-3 py-1 rounded-sm font-bold ${days === d ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
                data-testid={`agg-days-${d}`}
              >
                {d}d
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="agg-kpis">
        <div className="hw-card p-6 border-l-4 border-l-emerald-500">
          <div className="hw-label text-slate-500 flex items-center gap-1"><IndianRupee className="w-3 h-3" /> Revenue</div>
          <div className="hw-head text-3xl font-black text-slate-900 mt-1" data-testid="kpi-revenue">₹{Number(data.kpis.revenue.value).toLocaleString("en-IN")}</div>
          <div className="text-[10px] uppercase tracking-wider text-slate-400 mt-1">confirmed bookings</div>
        </div>
        <div className="hw-card p-6 border-l-4 border-l-blue-500">
          <div className="hw-label text-slate-500 flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Bookings</div>
          <div className="hw-head text-3xl font-black text-slate-900 mt-1" data-testid="kpi-bookings">{data.kpis.bookings.value.toLocaleString("en-IN")}</div>
          <div className="text-[10px] uppercase tracking-wider text-slate-400 mt-1">across all properties</div>
        </div>
        <div className="hw-card p-6 border-l-4 border-l-amber-500">
          <div className="hw-label text-slate-500 flex items-center gap-1"><Percent className="w-3 h-3" /> Occupancy</div>
          <div className="hw-head text-3xl font-black text-slate-900 mt-1" data-testid="kpi-occupancy">{data.kpis.occupancy.value}%</div>
          <div className="text-[10px] uppercase tracking-wider text-slate-400 mt-1">room-nights sold / avail</div>
        </div>
      </div>

      {/* Leaderboard */}
      <div className="hw-card p-6" data-testid="agg-leaderboard">
        <div className="hw-head text-lg font-black text-slate-900">Property leaderboard</div>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase text-slate-500 border-b">
              <tr>
                <th className="py-2">Rank</th>
                <th>Property</th>
                <th>City</th>
                <th className="text-right">Bookings</th>
                <th className="text-right">Revenue</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data.leaderboard.map((row, i) => (
                <tr key={row.hotel_id} className="border-b border-slate-100 hover:bg-emerald-50/30">
                  <td className="py-3">
                    <span className={`font-black ${i === 0 ? "text-amber-600" : i === 1 ? "text-slate-500" : i === 2 ? "text-amber-800" : "text-slate-400"}`}>#{i + 1}</span>
                  </td>
                  <td className="font-bold text-slate-900 flex items-center gap-2 py-3">
                    <Building2 className="w-4 h-4 text-slate-400" /> {row.name}
                  </td>
                  <td className="text-slate-600">{row.city}</td>
                  <td className="text-right font-bold">{row.bookings}</td>
                  <td className="text-right font-black text-emerald-700">₹{Number(row.revenue).toLocaleString("en-IN")}</td>
                  <td>
                    <button
                      onClick={() => gotoProperty(row.hotel_id)}
                      className="text-xs font-bold text-emerald-700 hover:underline flex items-center gap-1"
                      data-testid={`agg-goto-${row.hotel_id}`}
                    >
                      Open <ExternalLink className="w-3 h-3" />
                    </button>
                  </td>
                </tr>
              ))}
              {data.leaderboard.length === 0 && (
                <tr><td colSpan={6} className="py-6 text-center text-slate-500 italic">No confirmed bookings in this window yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
